import React, {useState} from 'react';
import cx from 'classnames';
import {ChooseDropdown, DropdownButtonTrigger} from '@priority-software/priority-style-react';
import {AIHelperAction, AIHelperToneOption} from './enums';
import AITexts from '../../Localization/AISearch/texts';
import {isRTL} from '../../Localization';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {prepareOptions} from './utils';
import {ModernTooltip} from '../Common/Tooltip/ModernTooltip';
import {monitorAction} from '../../services/datadog';
import {AIWritingAssistantMonitoring} from '../../constants/monitoring';
import styles from './styles.module.scss';

type Props = {
    onSelect: (actionName: string, actionValue?: string) => void;
    portalId?: string;
    linkHoverEffect?: boolean;
    disabled?: boolean;
};

type OptionExtraData = {
    type: string;
};

export const AIHelperDropdown = (props: Props) => {
    const {onSelect, portalId, linkHoverEffect, disabled} = props;
    const [actionOptions] = useState(prepareOptions(AIHelperAction, 'action'));
    const [extraOptions, setExtraOptions] = useState([]);
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    // Monitor AI Writing Assistant dropdown open
    const handleDropdownOpen = () => {
        if (!disabled) {
            try {
                monitorAction(AIWritingAssistantMonitoring.AI_mailedi_Icon, {
                    action: 'dropdown_opened',
                    component: 'AIHelperDropdown',
                    timestamp: Date.now()
                });
            } catch (error) {
                // Monitoring should not affect functionality
                console.warn('AI monitoring failed:', error);
            }
        }
    };

    // Monitor action and tone selections
    const monitorSelection = (actionType: string, optionValue: string) => {
        try {
            let monitoringKey: string;
            let monitoringData: any = {
                timestamp: Date.now(),
                component: 'AIHelperDropdown'
            };

            // Determine monitoring key based on action type and option
            if (actionType === AIHelperAction.TONE) {
                // Map tone options to monitoring keys
                const toneMonitoringMap = {
                    [AIHelperToneOption.FORMAL]: AIWritingAssistantMonitoring.AI_mailedi_formal,
                    [AIHelperToneOption.PROFESSIONAL]:
                        AIWritingAssistantMonitoring.AI_mailedi_professional,
                    [AIHelperToneOption.NEUTRAL]: AIWritingAssistantMonitoring.AI_mailedi_neutral,
                    [AIHelperToneOption.POLITE]: AIWritingAssistantMonitoring.AI_mailedi_polite,
                    [AIHelperToneOption.APPRECIATIVE]:
                        AIWritingAssistantMonitoring.AI_mailedi_appreciative
                };

                monitoringKey = toneMonitoringMap[optionValue as AIHelperToneOption];
                monitoringData = {
                    ...monitoringData,
                    action: 'tone_selected',
                    toneType: optionValue,
                    enhancementType: actionType
                };
            } else {
                // Map action types to monitoring keys
                const actionMonitoringMap = {
                    [AIHelperAction.TONE]: AIWritingAssistantMonitoring.AI_mailedi_tone,
                    [AIHelperAction.IMPROVE]: AIWritingAssistantMonitoring.AI_mailedi_imprv,
                    [AIHelperAction.SPELLING]: AIWritingAssistantMonitoring.AI_mailedi_spell
                };

                monitoringKey = actionMonitoringMap[actionType as AIHelperAction];
                monitoringData = {
                    ...monitoringData,
                    action: 'action_selected',
                    actionType: actionType
                };
            }

            if (monitoringKey) {
                monitorAction(monitoringKey, monitoringData);
            }
        } catch (error) {
            // Monitoring should not affect functionality
            console.warn('AI monitoring failed:', error);
        }
    };

    const onChange = (selected: DropdownItemData) => {
        const data = selected.data as OptionExtraData;
        const isToneAction = selected.value === AIHelperAction.TONE;
        // Length option was removed from AI for now
        // const isLengthAction = selected.value === AIHelperAction.LENGTH;

        if (isToneAction) {
            setExtraOptions(prepareOptions(AIHelperToneOption, AIHelperAction.TONE));
            // Monitor tone action selection
            monitorSelection(AIHelperAction.TONE, '');
            return;
        }

        // Length option was removed from AI for now
        // if (isLengthAction) {
        //     setExtraOptions(prepareOptions(AIHelperLengthOption, AIHelperAction.LENGTH));
        //     return;
        // }

        const hasExtraOptions = extraOptions.length > 0;

        if (hasExtraOptions) {
            // Monitor tone option selection
            monitorSelection(data.type, selected.value as string);
            onSelect?.(data.type, selected.value as string);
        } else {
            // Monitor action selection
            monitorSelection(selected.value as string, '');
            onSelect?.(selected.value as string, ''); // Handle action selection
        }
        if (selected.value !== AIHelperAction.TONE) {
            // Close the dropdown after selecting an action
            triggerRef.current.click();
        }
    };

    return (
        <ChooseDropdown
            data={extraOptions.length > 0 ? extraOptions : actionOptions}
            isRtl={isRTL()}
            itemBorder={true}
            width={200}
            onChange={onChange}
            onOpen={handleDropdownOpen}
            openingSide={'leftSide'}
            componentClasses={cx(styles.component, 'pui-ai-writing-assistant')}
            trigger={
                <ModernTooltip
                    portalId={portalId}
                    tooltip={AITexts.ai_assistant_tooltip}
                    delay={300}
                    position="top-end"
                >
                    <DropdownButtonTrigger
                        ref={triggerRef}
                        content={AITexts.ai_assistant_label}
                        size="small"
                        skin="aierp"
                        componentClasses={cx(styles.trigger, {
                            [styles.linkHover]: linkHoverEffect,
                            [styles.disabled]: disabled
                        })}
                        suffixIcon={null}
                        disabled={disabled}
                    />
                </ModernTooltip>
            }
            usePortal
            portalSelector={portalId}
            defaultFocusedIndex={-1}
            maxScrollHeight={320}
            closeOnSelect={false}
            onClose={() => {
                // Reset extra options when the dropdown is closed
                setExtraOptions([]);
            }}
        />
    );
};
